import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CRM_API } from "../api";
import { isNewLead, markAllSeen, isNewApproval, markAllApprovalsSeen } from "../leadSeen";

const POLL_MS = 45_000;

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [880, 1174.66]; // A5 -> D6 two-tone chime
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {
    /* audio unavailable — badge still works */
  }
}

export default function LeadNotifier() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const prevCount = useRef(null);

  const check = useCallback(async () => {
    try {
      const [quotesRes, apptsRes, estRes] = await Promise.all([
        CRM_API.get("/quotes"),
        CRM_API.get("/appointments"),
        CRM_API.get("/estimates", { params: { limit: 100 } }),
      ]);
      const quotes = Array.isArray(quotesRes.data) ? quotesRes.data : [];
      const appts = Array.isArray(apptsRes.data) ? apptsRes.data : [];
      const ests = Array.isArray(estRes.data) ? estRes.data : [];
      const newQuotes = quotes.filter(
        (q) => !q.appointment_id && !["done", "cancelled"].includes(q.status) && isNewLead(q)
      ).length;
      const newBookings = appts.filter(
        (a) =>
          a.created_by === "website" &&
          !["cancelled", "no_show", "completed"].includes(a.status) &&
          isNewLead(a)
      ).length;
      // Estimate approvals by customers (public link) — staff must see these
      const newApprovals = ests.filter(isNewApproval).length;
      const total = newQuotes + newBookings + newApprovals;
      if (prevCount.current !== null && total > prevCount.current) playChime();
      prevCount.current = total;
      setCount(total);
    } catch {
      /* network hiccup — retry next poll */
    }
  }, []);

  useEffect(() => {
    check();
    const iv = setInterval(check, POLL_MS);
    return () => clearInterval(iv);
  }, [check]);

  const open = () => {
    markAllSeen();
    markAllApprovalsSeen();
    prevCount.current = 0;
    setCount(0);
    navigate("/crm");
  };

  return (
    <button
      data-testid="new-leads-bell"
      onClick={open}
      title="New website requests"
      className="relative p-2 text-white/70 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
    >
      <Bell className="w-5 h-5" strokeWidth={1.75} />
      {count > 0 && (
        <span
          data-testid="new-leads-badge"
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
